import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import {
  MINI_APP_DEFAULT_ROUTE,
  incompleteOnboardingUrl,
  miniTenantAwaitingApproval,
  userHasMiniAppAdminRole,
  userHasMiniStoreAdminRole,
} from 'app/core/auth/utils/mini-app-admin.utils';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RoleRedirectService {
  private _userService = inject(UserService);
  private _router = inject(Router);

  /**
   * Get the default redirect URL based on user role
   */
  getDefaultRedirectUrl(): string {
    const currentUser = this._userService.currentUser;
    if (currentUser) {
      return this.getRedirectUrlForUser(currentUser);
    }
    return '/dashboard'; // fallback
  }

  /**
   * Get redirect URL as Observable
   */
  getDefaultRedirectUrl$() {
    return this._userService.user$.pipe(
      map((user) => {
        if (user) {
          return this.getRedirectUrlForUser(user);
        }
        return '/dashboard'; // fallback
      }),
      catchError(() => of('/dashboard')), // fallback on error
    );
  }

  /**
   * Navigate to the appropriate default page based on user role
   */
  navigateToDefaultPage(): void {
    this.getDefaultRedirectUrl$().subscribe((url) => {
      this._router.navigateByUrl(url);
    });
  }

  /**
   * Get redirect URL for a specific user
   */
  private getRedirectUrlForUser(user: any): string {
    if (!user || !user.roles || user.roles.length === 0) {
      return '/dashboard';
    }

    const roleNames = user.roles.map((role: any) => role.role?.toLowerCase() || '');

    // Check for super admin role (highest priority)
    // Super Admin should NEVER be redirected to onboarding
    const isSuperAdmin = roleNames.includes('super admin') || roleNames.includes('superadmin');
    
    if (isSuperAdmin) {
      return '/saas-overview';
    }

    if (miniTenantAwaitingApproval(user)) {
      return incompleteOnboardingUrl(user);
    }

    // Mini Store Admin is store-scoped and skips tenant onboarding
    if (userHasMiniStoreAdminRole(user.roles)) {
      return MINI_APP_DEFAULT_ROUTE;
    }

    // Check if user has completed onboarding (except Super Admin and Mini Store Admin)
    if (!user.onboardingComplete) {
      // User hasn't completed onboarding, redirect to onboarding
      return incompleteOnboardingUrl(user);
    }

    // Check for mini tenant admin or mini store admin role
    if (userHasMiniAppAdminRole(user.roles)) {
      return MINI_APP_DEFAULT_ROUTE;
    }

    // Check for tenant admin role
    if (roleNames.includes('tenant admin') || roleNames.includes('tenantadmin')) {
      return '/dashboard';
    }

    // Check for store manager role
    if (roleNames.includes('store manager') || roleNames.includes('storemanager')) {
      return '/dashboard'; // or whatever default you want for store manager
    }

    // Check for staff/cashier role
    if (roleNames.includes('cashier') || roleNames.includes('staff')) {
      return '/dashboard'; // or whatever default you want for staff
    }

    // Default fallback
    return '/dashboard';
  }
}
